import { apiClient } from '@/shared/api/client';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import type {
  BoardListParams,
  CreatePostInput,
  Paginated,
  Post,
  UpdatePostInput,
} from '../types/board.type.ts';

/**
 * 게시판 CRUD 호출부.
 *
 * ── MSA 관점 ─────────────────────────────────────────────────────
 * - JWT 는 Gateway 에서 검증됨. 이 클라이언트는 쿠키만 싣고(withCredentials),
 *   다운스트림 서비스는 Gateway 가 주입한 x-user-id/x-user-role 을 신뢰한다.
 * - FE 는 토큰을 직접 다루지 않는다(HttpOnly). 권한 UI 분기는 /auth/me 결과로.
 * - 목록/상세는 public(비로그인 열람 가능) → skipAuthRedirect 로 강제 로그인 방지.
 * - 생성/수정/삭제는 protected → 401 시 refresh→재시도, 실패 시 로그인 리다이렉트.
 */
export const boardApi = {
  /** 목록 (offset 페이지네이션 + 검색/카테고리). 비로그인 열람 가능. */
  getList: async (
    type: BoardType,
    params: BoardListParams,
  ): Promise<Paginated<Post>> => {
    const { data } = await apiClient.get<Paginated<Post>>(
      API_ENDPOINTS.news.list(type),
      { params, skipAuthRedirect: true },
    );
    return data;
  },

  /** 단건 상세 (본문 body + attachments 포함). 비로그인 열람 가능. */
  getDetail: async (type: BoardType, id: string | number): Promise<Post> => {
    const { data } = await apiClient.get<Post>(
      API_ENDPOINTS.news.detail(type, id),
      { skipAuthRedirect: true },
    );
    return data;
  },

  /** 생성 (protected). 서버가 authorId 를 x-user-id 로 채운다. */
  create: async (type: BoardType, input: CreatePostInput): Promise<Post> => {
    const { data } = await apiClient.post<Post>(
      API_ENDPOINTS.news.create(type),
      input,
    );
    return data;
  },

  /** 수정 (protected, PUT 전체 교체). */
  update: async (
    type: BoardType,
    id: string | number,
    input: UpdatePostInput,
  ): Promise<Post> => {
    const { data } = await apiClient.put<Post>(
      API_ENDPOINTS.news.update(type, id),
      input,
    );
    return data;
  },

  /** 삭제 (protected). */
  remove: async (type: BoardType, id: string | number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.news.remove(type, id));
  },
};
